"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { isLocationDateActive } from "@/lib/location";
import {
  PROJECT_LOCATIONS_BROADCAST_CHANNEL,
  type ProjectLocationsBroadcastMessage,
} from "@/lib/project/project-locations-broadcast";
import { useActiveProject } from "./active-project";

/** How often to revalidate project locations while the tab is visible (other users’ edits). */
const POLL_INTERVAL_MS = 60_000 * 5;

/** Location linked to the active project (from `/api/projects/[projectId]/locations`). */
export type ProjectLocationRecord = {
  id: string;
  organizationId: string;
  name: string;
  typeId: string;
  type: { id: string; title: string; icon: string };
  lat: number;
  lon: number;
  timezone: string;
  startDate: string | null;
  endDate: string | null;
};

export type ProjectLocationsContextValue = {
  /** Active project id used for the last fetch (null = none). */
  projectId: string | null;
  isLoading: boolean;
  error: string | null;
  /** All locations assigned to the active project. */
  allLocations: ProjectLocationRecord[];
  /** Locations in `allLocations` whose start/end dates include today (UTC). */
  activeLocations: ProjectLocationRecord[];
  /** Current snapshot: all locations for the active project. */
  getAllLocations: () => ProjectLocationRecord[];
  /** Current snapshot: date-active subset for the active project. */
  getActiveLocations: () => ProjectLocationRecord[];
  /** Re-fetch from the API for the current active project. */
  refresh: () => void;
};

const ProjectLocationsContext = createContext<ProjectLocationsContextValue | null>(null);

export function ProjectLocationsProvider({ children }: { children: React.ReactNode }) {
  const { activeProjectId } = useActiveProject();
  const [allLocations, setAllLocations] = useState<ProjectLocationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const listEtagRef = useRef<string | null>(null);
  const etagProjectRef = useRef<string | null>(null);

  useEffect(() => {
    if (!activeProjectId) {
      listEtagRef.current = null;
      etagProjectRef.current = null;
      setAllLocations([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (etagProjectRef.current !== activeProjectId) {
      listEtagRef.current = null;
      etagProjectRef.current = activeProjectId;
      setAllLocations([]);
      setError(null);
    }

    const ac = new AbortController();
    void (async () => {
      try {
        const headers: HeadersInit = {};
        if (!listEtagRef.current) {
          setIsLoading(true);
        }

        if (listEtagRef.current) {
          headers["If-None-Match"] = listEtagRef.current;
        }
        const res = await fetch(`/api/projects/${encodeURIComponent(activeProjectId)}/locations`, {
          signal: ac.signal,
          credentials: "same-origin",
          headers,
        });
        if (res.status === 304) {
          return;
        }

        setError(null);

        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as {
            error?: string;
          } | null;
          throw new Error(body?.error ?? res.statusText);
        }
        const nextEtag = res.headers.get("ETag");
        if (nextEtag) {
          listEtagRef.current = nextEtag;
        }
        const data = (await res.json()) as { locations?: ProjectLocationRecord[] };
        if (!ac.signal.aborted) {
          setAllLocations(data.locations ?? []);
        }
      } catch (e) {
        if (e instanceof Error && e.name === "AbortError") return;
        if (!ac.signal.aborted) {
          listEtagRef.current = null;
          setError(e instanceof Error ? e.message : "Failed to load locations");
          setAllLocations([]);
        }
      } finally {
        if (!ac.signal.aborted) {
          setIsLoading(false);
        }
      }
    })();

    return () => ac.abort();
  }, [activeProjectId, refreshToken]);

  useEffect(() => {
    if (!activeProjectId) return;
    const id = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      setRefreshToken((n) => n + 1);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [activeProjectId]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "visible" || !activeProjectId) return;
      setRefreshToken((n) => n + 1);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [activeProjectId]);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const bc = new BroadcastChannel(PROJECT_LOCATIONS_BROADCAST_CHANNEL);
    bc.onmessage = (ev: MessageEvent<ProjectLocationsBroadcastMessage>) => {
      const data = ev.data;
      if (data?.type !== "project-locations-changed" || !activeProjectId) {
        return;
      }
      const ids = data.projectIds;
      if (ids != null && ids.length > 0 && !ids.includes(activeProjectId)) {
        return;
      }
      listEtagRef.current = null;
      setRefreshToken((n) => n + 1);
    };
    return () => bc.close();
  }, [activeProjectId]);

  const activeLocations = useMemo(
    () => allLocations.filter((loc) => isLocationDateActive(loc.startDate, loc.endDate)),
    [allLocations],
  );

  const refresh = useCallback(() => {
    if (!activeProjectId) return;
    listEtagRef.current = null;
    setRefreshToken((n) => n + 1);
  }, [activeProjectId]);

  const getAllLocations = useCallback(() => allLocations, [allLocations]);

  const getActiveLocations = useCallback(() => activeLocations, [activeLocations]);

  const value = useMemo(
    (): ProjectLocationsContextValue => ({
      projectId: activeProjectId,
      isLoading,
      error,
      allLocations,
      activeLocations,
      getAllLocations,
      getActiveLocations,
      refresh,
    }),
    [
      activeProjectId,
      isLoading,
      error,
      allLocations,
      activeLocations,
      getAllLocations,
      getActiveLocations,
      refresh,
    ],
  );

  return (
    <ProjectLocationsContext.Provider value={value}>{children}</ProjectLocationsContext.Provider>
  );
}

export function useProjectLocations(): ProjectLocationsContextValue {
  const ctx = useContext(ProjectLocationsContext);
  if (!ctx) {
    throw new Error("useProjectLocations must be used within ProjectLocationsProvider");
  }
  return ctx;
}
